import clsx from 'clsx';
import { memo, useMemo, useState } from 'react';

import Image from '../Image';
import { useTranslateFn } from '../Translate';

import { getLink } from './utils';

import { trackSponsorElement } from '@services/GoogleTagManagerService';
import type { Sponsor } from '@shared-types/sponsor.types';

import * as classes from './styles.module.scss';

export type SingleSponsorProps = {
  sponsor: Sponsor;
  showSponsorName?: boolean;
  slotId?: string;
  position?:
    | 'header'
    | 'footer'
    | 'body'
    | 'liveblog_match'
    | 'liveblog_draw'
    | 'card_liveblog'
    | 'card_homepage'
    | 'card_fixtures'
    | 'card_news';
  creative?: 'all-sponsors' | 'editorial-post' | 'exclusive-section' | 'share-of-voice' | 'branded-card';
};

const SingleSponsor = ({ sponsor, showSponsorName, slotId, position, creative }: SingleSponsorProps) => {
  const t = useTranslateFn();

  const link = useMemo(() => getLink(sponsor), [sponsor]);
  const [hasError, setHasError] = useState(false);

  const trackingData = useMemo(() => {
    if (!sponsor) return null;
    return {
      id: slotId ?? `${sponsor.code}-${creative}-${position}`,
      name: sponsor.code,
      creative,
      position,
    };
  }, [sponsor, position, creative, slotId]);

  const handleImageError = () => {
    setHasError(true);
  };

  const image = useMemo(
    () => (
      <Image
        className={classes.sponsorItemImg}
        onError={handleImageError}
        src={sponsor.image}
        alt={sponsor.name}
        title={sponsor.name}
      />
    ),
    [sponsor.image, sponsor.name],
  );

  return (
    <li ref={trackSponsorElement(trackingData)} className={clsx(classes.sponsorItem, hasError && 'hidden')}>
      {link ? (
        <a className={classes.sponsorItemWrap} href={link} target='_blank' title={sponsor.name}>
          {image}
        </a>
      ) : (
        <div className={classes.sponsorItemWrap}>{image}</div>
      )}
      {showSponsorName && <div className={classes.sponsorItemName}>{t(sponsor.name)}</div>}
    </li>
  );
};

export default memo(SingleSponsor);
